/**
 * "Paciente no SISAC" (#1024): o contato e o paciente dele na clínica.
 *
 * Vinculado, mostra o que o SISAC tem agora (nome, nascimento, código, o fim do
 * CPF e as últimas consultas) e o "Desvincular". Sem vínculo, quem edita o
 * cliente procura pelo CPF, pelo nome ou código (quando a clínica ligou essa
 * busca) e pelo telefone da conversa, escolhe o paciente e
 * vincula; quem o SISAC não tem, cadastra ali mesmo, e o vínculo vem junto.
 *
 * ── Achado pelo telefone é sugestão ──────────────────────────────────────
 *
 * A mesma linha costuma ser da família inteira. A lista diz por onde cada um
 * foi achado, o CPF vem primeiro, e o vínculo é sempre um clique de quem
 * atende: nada se vincula sozinho.
 *
 * ── A confirmação fica no card ───────────────────────────────────────────
 *
 * Sem toast (decisão de 23/10): "Paciente vinculado." aparece como linha de
 * status dentro do card, como o "Consulta salva." da aba Consulta.
 */

import { useState, type ReactNode } from 'react';
import type { CandidatosAPaciente, PacienteDoCliente } from '@cliqcrm/contracts';
import { Botao, Campo, CampoSelecao, Dialogo, Ficha, Selo } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  NOME_DA_SITUACAO,
  cpfCompleto,
  cpfProtegido,
  diaNaTela,
  mascararCpf,
  soDigitos,
} from '@/entities/saude/paciente';
import {
  corpoDoCadastro,
  cpfJaCadastrado,
  errosDoCadastro,
  rascunhoInicial,
  type RascunhoDoCadastro,
} from '../lib/cadastro';
import { pedidoDaBusca, textoDaBusca } from '../lib/busca';
import { recadoDoSisac, usePacienteNoSisac } from '../model/usar-paciente-no-sisac';
import { UltimasVezes } from './ultimas-vezes';
import { idadeEmAnos, iniciais, partesDaConsulta } from '../lib/vinculado';
import './paciente-no-sisac.css';

const ESPERA = 'Aguarde a resposta do SISAC.';

/** O dia de hoje no Brasil, para o nascimento não cair no futuro perto da meia-noite. */
function hojeNoBrasil(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

/** "qua, 01/11/2026 10:00", sem passar por fuso: a hora é a da clínica. */
function quandoNaTela(dataHora: string | null): string {
  const m = dataHora?.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : 'Sem data';
}

const TOM_DA_SITUACAO = {
  agendada: 'info',
  confirmada: 'ok',
  atendida: 'neutro',
  falta: 'atencao',
  outra: 'rascunho',
} as const;

export interface ContatoDoPaciente {
  readonly nome: string | null;
  /** O telefone principal do contato, como o CRM o tem. */
  readonly telefone: string | null;
  readonly email: string | null;
  /** O nascimento da ficha do paciente, quando há, para o cadastro. */
  readonly nascimento?: string | null;
}

export function PacienteNoSisac({
  clienteId,
  contato,
  podeEditar,
}: {
  clienteId: string;
  contato: ContatoDoPaciente;
  podeEditar: boolean;
}): ReactNode {
  const p = usePacienteNoSisac(clienteId, true);
  const [texto, definirTexto] = useState('');
  const [cadastro, definirCadastro] = useState<RascunhoDoCadastro | null>(null);
  const [confirmandoSaida, definirConfirmandoSaida] = useState(false);
  const [vendoUltimasVezes, definirVendoUltimasVezes] = useState(false);

  if (p.leitura.isPending)
    return <Carregando forma="linha" linhas={2} rotulo="Lendo o paciente no SISAC…" />;
  if (p.leitura.isError) {
    return (
      <Faixa tom="bloqueado">
        {recadoDoSisac(p.leitura.error, 'Não foi possível ler o paciente no SISAC.')}
      </Faixa>
    );
  }
  const dados = p.leitura.data;
  const aviso = p.aviso ? (
    <p className="paciente-no-sisac__aviso" role="status">
      {p.aviso}
    </p>
  ) : null;

  if (dados.vinculo) {
    return (
      <div className="paciente-no-sisac">
        {aviso}
        <Vinculado dados={dados} />
        {(podeEditar || dados.pode.ver_ultimas_vezes) && (
          <div className="paciente-no-sisac__acoes">
            {dados.pode.ver_ultimas_vezes && (
              <Botao variante="secundario" miudo onClick={() => definirVendoUltimasVezes(true)}>
                Últimas vezes
              </Botao>
            )}
            {podeEditar && (
              <Botao variante="fantasma" miudo onClick={() => definirConfirmandoSaida(true)}>
                Desvincular
              </Botao>
            )}
          </div>
        )}
        {vendoUltimasVezes && (
          <UltimasVezes
            clienteId={clienteId}
            nome={dados.paciente?.nome ?? null}
            aoFechar={() => definirVendoUltimasVezes(false)}
          />
        )}
        {confirmandoSaida && (
          <Dialogo
            titulo="Desvincular o paciente?"
            largura="estreita"
            consequencia
            aoFechar={() => definirConfirmandoSaida(false)}
            botoes={
              <>
                <Botao variante="fantasma" onClick={() => definirConfirmandoSaida(false)}>
                  Cancelar
                </Botao>
                <Botao
                  variante="perigo"
                  desabilitado={p.desvincular.isPending}
                  motivo="Desvinculando…"
                  onClick={() =>
                    p.desvincular.mutate(undefined, {
                      onSuccess: () => definirConfirmandoSaida(false),
                    })
                  }
                >
                  {p.desvincular.isPending ? 'Desvinculando…' : 'Desvincular'}
                </Botao>
              </>
            }
          >
            <p>
              O contato deixa de apontar para este paciente. Nada muda no SISAC, e dá para vincular
              de novo depois.
            </p>
            {p.desvincular.isError && (
              <Faixa tom="bloqueado">
                {recadoDoSisac(p.desvincular.error, 'Não foi possível desvincular.')}
              </Faixa>
            )}
          </Dialogo>
        )}
      </div>
    );
  }

  if (!podeEditar) {
    return (
      <p className="paciente-no-sisac__nota">Este contato não está vinculado a um paciente.</p>
    );
  }

  if (cadastro) {
    return (
      <Cadastro
        rascunho={cadastro}
        aoMudar={definirCadastro}
        aoCancelar={() => {
          p.cadastrar.reset();
          definirCadastro(null);
        }}
        aoEnviar={(r) => p.cadastrar.mutate(corpoDoCadastro(r))}
        enviando={p.cadastrar.isPending}
        cpfRepetido={cpfJaCadastrado(p.cadastrar.error)}
        aoConfirmarCpfRepetido={(r) => p.cadastrar.mutate(corpoDoCadastro(r, true))}
        aoDesistirDoCpfRepetido={() => p.cadastrar.reset()}
        erro={
          p.cadastrar.isError && !cpfJaCadastrado(p.cadastrar.error)
            ? recadoDoSisac(p.cadastrar.error, 'Não foi possível cadastrar no SISAC.')
            : null
        }
      />
    );
  }

  const comTelefone = dados.pode.buscar_por_telefone && soDigitos(contato.telefone ?? '') !== '';
  const porTermo = dados.pode.buscar_por_termo;
  const pedido = pedidoDaBusca(texto, { porTermo, comTelefone });
  const podeProcurar = !p.busca.isPending && pedido !== null;
  const resultado = p.busca.data;
  const ajudaDaBusca = [
    porTermo ? 'Nome e sobrenome, código ou CPF.' : null,
    comTelefone
      ? 'Procura também pelo telefone desta conversa.'
      : 'Depois da busca, o CPF aparece só com os dois últimos dígitos.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="paciente-no-sisac">
      {aviso}
      <form
        className="paciente-no-sisac__busca"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (podeProcurar && pedido) p.busca.mutate(pedido);
        }}
      >
        <Campo
          id={`paciente-no-sisac-cpf-${clienteId}`}
          rotulo={porTermo ? 'CPF, nome ou código do paciente' : 'CPF do paciente'}
          inputMode={porTermo ? 'text' : 'numeric'}
          autoComplete="off"
          placeholder={porTermo ? 'Ex.: maria souza, 000777 ou 000.000.000-00' : '000.000.000-00'}
          ajuda={ajudaDaBusca}
          value={texto}
          onChange={(e) => definirTexto(textoDaBusca(e.currentTarget.value, porTermo))}
        />
        <Botao
          variante="secundario"
          type="submit"
          desabilitado={!podeProcurar}
          motivo={
            p.busca.isPending
              ? ESPERA
              : porTermo
                ? 'Digite o CPF, o código, ou o nome e o sobrenome.'
                : 'Digite os 11 dígitos do CPF.'
          }
        >
          {p.busca.isPending ? 'Procurando…' : 'Procurar no SISAC'}
        </Botao>
      </form>

      {p.busca.isError && (
        <Faixa tom="bloqueado">
          {recadoDoSisac(p.busca.error, 'Não foi possível procurar no SISAC.')}
        </Faixa>
      )}

      {resultado && (
        <Achados
          resultado={resultado}
          vinculando={p.vincular.isPending ? (p.vincular.variables ?? null) : null}
          aoVincular={(codigo) => p.vincular.mutate(codigo)}
          erro={
            p.vincular.isError
              ? recadoDoSisac(p.vincular.error, 'Não foi possível vincular.')
              : null
          }
          podeCadastrar={dados.pode.cadastrar}
          aoCadastrar={() =>
            definirCadastro(
              rascunhoInicial({
                nome: contato.nome,
                telefone: contato.telefone,
                email: contato.email,
                nascimento: contato.nascimento ?? null,
                cpf: cpfCompleto(texto) && pedido?.cpf ? mascararCpf(texto) : '',
              }),
            )
          }
        />
      )}
    </div>
  );
}

function Vinculado({ dados }: { dados: PacienteDoCliente }): ReactNode {
  const { paciente, vinculo } = dados;
  const nascimento = diaNaTela(paciente?.nascimento ?? null);
  const idade = idadeEmAnos(paciente?.nascimento ?? null, new Date());
  const nome = paciente?.nome ?? 'Paciente vinculado';
  return (
    <>
      {dados.sisac === 'fora' && (
        <Faixa tom="atencao">
          O SISAC não respondeu agora. O vínculo continua; os dados do paciente voltam quando ele
          responder.
        </Faixa>
      )}
      {dados.sisac === 'ok' && !paciente && (
        <Faixa tom="atencao">
          O SISAC não tem mais o paciente deste vínculo. Desvincule e procure de novo.
        </Faixa>
      )}
      <div className="paciente-no-sisac__cartao">
        <div className="paciente-no-sisac__cabeca">
          <span className="paciente-no-sisac__iniciais" aria-hidden="true">
            {iniciais(paciente?.nome ?? null)}
          </span>
          <div className="paciente-no-sisac__identidade">
            <strong className="paciente-no-sisac__nome">{nome}</strong>
            <Selo tom="ok">Vinculado ao contato</Selo>
          </div>
        </div>
        <Ficha
          larguraRotulo="96px"
          linhas={[
            {
              rotulo: 'Código',
              valor: <span className="paciente-no-sisac__codigo">{vinculo?.codigo}</span>,
            },
            {
              rotulo: 'Nascimento',
              valor: nascimento
                ? `${nascimento}${idade !== null ? ` · ${idade} ${idade === 1 ? 'ano' : 'anos'}` : ''}`
                : 'Não informado',
              vazio: !nascimento,
            },
            {
              rotulo: 'CPF',
              valor: paciente?.cpf_final ? cpfProtegido(paciente.cpf_final) : 'Não informado',
              vazio: !paciente?.cpf_final,
            },
          ]}
        />
      </div>
      {dados.consultas.length > 0 && (
        <div className="paciente-no-sisac__agenda">
          <h4 className="paciente-no-sisac__titulo-agenda">Consultas no SISAC</h4>
          <ul className="paciente-no-sisac__consultas" aria-label="Últimas consultas no SISAC">
            {dados.consultas.map((consulta, i) => {
              const quando = partesDaConsulta(consulta.data_hora);
              return (
                <li
                  key={`${consulta.data_hora ?? 'sem-data'}-${i}`}
                  className="paciente-no-sisac__consulta"
                >
                  <span className="paciente-no-sisac__data" aria-hidden="true">
                    <span className="paciente-no-sisac__data-dia">{quando?.dia ?? '–'}</span>
                    <span className="paciente-no-sisac__data-mes">{quando?.mes ?? ''}</span>
                  </span>
                  <span className="paciente-no-sisac__detalhe">
                    <span className="paciente-no-sisac__linha-forte">
                      {/* O leitor de tela ouve a data inteira; o bloco ao lado é só desenho. */}
                      <span className="visualmente-oculto">
                        {quandoNaTela(consulta.data_hora)}{' '}
                      </span>
                      {quando?.hora
                        ? `${quando.semana}, ${quando.hora}`
                        : (quando?.semana ?? 'Sem data')}
                      {consulta.medico ? ` · ${consulta.medico}` : ''}
                    </span>
                    {consulta.especialidade && (
                      <span className="paciente-no-sisac__linha-fraca">
                        {consulta.especialidade}
                      </span>
                    )}
                  </span>
                  <Selo tom={TOM_DA_SITUACAO[consulta.situacao]}>
                    {NOME_DA_SITUACAO[consulta.situacao]}
                  </Selo>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {dados.sisac === 'parcial' && (
        <p className="paciente-no-sisac__nota">As consultas não vieram do SISAC agora.</p>
      )}
    </>
  );
}

const DITO_DO_CAMINHO = {
  cpf: 'por este CPF',
  termo: 'por este nome ou código',
  telefone: 'pelo telefone da conversa',
} as const;

/** "por este CPF nem pelo telefone da conversa": por onde a busca procurou. */
function ondeProcurou(caminhos: CandidatosAPaciente['buscou_por']): string {
  return caminhos.map((caminho) => DITO_DO_CAMINHO[caminho]).join(' nem ');
}

function Achados({
  resultado,
  vinculando,
  aoVincular,
  erro,
  podeCadastrar,
  aoCadastrar,
}: {
  resultado: CandidatosAPaciente;
  vinculando: string | null;
  aoVincular: (codigo: string) => void;
  erro: string | null;
  podeCadastrar: boolean;
  aoCadastrar: () => void;
}): ReactNode {
  const cadastrar = podeCadastrar ? (
    <Botao variante="secundario" miudo onClick={aoCadastrar}>
      Cadastrar no SISAC
    </Botao>
  ) : null;

  if (resultado.candidatos.length === 0) {
    return (
      <div className="paciente-no-sisac__vazio" role="status">
        <p>Não encontramos no SISAC {ondeProcurou(resultado.buscou_por)}.</p>
        {cadastrar}
      </div>
    );
  }

  return (
    <div className="paciente-no-sisac__achados">
      {resultado.cortada && (
        <p className="paciente-no-sisac__nota" role="status">
          {resultado.buscou_por.includes('termo')
            ? 'Há mais pacientes com esse nome. Digite também o sobrenome para achar mais rápido.'
            : 'O SISAC mandou só parte dos pacientes achados. Procure pelo CPF para achar o certo.'}
        </p>
      )}
      <ul className="paciente-no-sisac__lista" aria-label="Pacientes encontrados no SISAC">
        {resultado.candidatos.map((candidato) => {
          const nascimento = diaNaTela(candidato.nascimento);
          return (
            <li key={candidato.codigo} className="paciente-no-sisac__candidato">
              <div className="paciente-no-sisac__paciente">
                <strong>{candidato.nome ?? `Paciente ${candidato.codigo}`}</strong>
                <span className="paciente-no-sisac__meta">
                  {nascimento ? `nasc. ${nascimento}` : 'Sem nascimento'}
                  {candidato.cpf_final ? ` · CPF ${cpfProtegido(candidato.cpf_final)}` : ''}
                  {` · código ${candidato.codigo}`}
                </span>
                <span className="paciente-no-sisac__selos">
                  {candidato.achado_por.includes('cpf') && <Selo tom="ok">pelo CPF</Selo>}
                  {candidato.achado_por.includes('termo') && (
                    <Selo tom="ok">pelo nome ou código</Selo>
                  )}
                  {candidato.achado_por.includes('telefone') && (
                    <Selo tom="info">pelo telefone</Selo>
                  )}
                </span>
              </div>
              {candidato.vinculado_a_outro ? (
                <p className="paciente-no-sisac__nota">Já vinculado a outro contato.</p>
              ) : (
                <Botao
                  variante="primario"
                  miudo
                  desabilitado={vinculando !== null}
                  motivo={ESPERA}
                  onClick={() => aoVincular(candidato.codigo)}
                >
                  {vinculando === candidato.codigo ? 'Vinculando…' : 'Vincular ao contato'}
                </Botao>
              )}
            </li>
          );
        })}
      </ul>
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
      {cadastrar && (
        <div className="paciente-no-sisac__acoes">
          <span className="paciente-no-sisac__nota">Não é nenhum destes?</span>
          {cadastrar}
        </div>
      )}
    </div>
  );
}

function Cadastro({
  rascunho,
  aoMudar,
  aoCancelar,
  aoEnviar,
  enviando,
  cpfRepetido,
  aoConfirmarCpfRepetido,
  aoDesistirDoCpfRepetido,
  erro,
}: {
  rascunho: RascunhoDoCadastro;
  aoMudar: (r: RascunhoDoCadastro) => void;
  aoCancelar: () => void;
  aoEnviar: (r: RascunhoDoCadastro) => void;
  enviando: boolean;
  /** O SISAC já tem paciente com este CPF: a pergunta antes de cadastrar outro. */
  cpfRepetido: boolean;
  aoConfirmarCpfRepetido: (r: RascunhoDoCadastro) => void;
  aoDesistirDoCpfRepetido: () => void;
  erro: string | null;
}): ReactNode {
  const [tentou, definirTentou] = useState(false);
  const erros = errosDoCadastro(rascunho, hojeNoBrasil());
  const valido = Object.keys(erros).length === 0;
  const mostrar = (campo: keyof RascunhoDoCadastro) => (tentou ? erros[campo] : undefined);
  const mudar = (campo: keyof RascunhoDoCadastro, valor: string) =>
    aoMudar({ ...rascunho, [campo]: valor });

  return (
    <form
      className="paciente-no-sisac paciente-no-sisac__cadastro"
      aria-label="Cadastrar no SISAC"
      onSubmit={(evento) => {
        evento.preventDefault();
        definirTentou(true);
        if (valido && !enviando) aoEnviar(rascunho);
      }}
    >
      <p className="paciente-no-sisac__nota">
        O paciente é criado no SISAC com estes dados e o contato já fica vinculado a ele.
      </p>
      <Campo
        id="paciente-no-sisac-nome"
        rotulo="Nome completo"
        value={rascunho.nome}
        erro={mostrar('nome')}
        onChange={(e) => mudar('nome', e.currentTarget.value)}
      />
      <Campo
        id="paciente-no-sisac-cadastro-cpf"
        rotulo="CPF"
        inputMode="numeric"
        autoComplete="off"
        value={rascunho.cpf}
        erro={mostrar('cpf')}
        onChange={(e) => mudar('cpf', mascararCpf(e.currentTarget.value))}
      />
      <div className="paciente-no-sisac__linha">
        <CampoSelecao
          id="paciente-no-sisac-sexo"
          rotulo="Sexo"
          value={rascunho.sexo}
          erro={mostrar('sexo')}
          onChange={(e) => mudar('sexo', e.currentTarget.value)}
        >
          <option value="">Escolha</option>
          <option value="F">Feminino</option>
          <option value="M">Masculino</option>
        </CampoSelecao>
        <Campo
          id="paciente-no-sisac-nascimento"
          rotulo="Nascimento"
          type="date"
          value={rascunho.nascimento}
          erro={mostrar('nascimento')}
          onChange={(e) => mudar('nascimento', e.currentTarget.value)}
        />
      </div>
      <Campo
        id="paciente-no-sisac-celular"
        rotulo="Celular com DDD"
        inputMode="tel"
        value={rascunho.celular}
        erro={mostrar('celular')}
        onChange={(e) => mudar('celular', e.currentTarget.value)}
      />
      <Campo
        id="paciente-no-sisac-email"
        rotulo="E-mail (opcional)"
        type="email"
        value={rascunho.email}
        erro={mostrar('email')}
        onChange={(e) => mudar('email', e.currentTarget.value)}
      />
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
      {cpfRepetido && (
        /*
         * A mesma pergunta do SISAC. "Voltar" deixa o formulário como está, para
         * a pessoa procurar o paciente que já existe; "Cadastrar outro" grava um
         * novo com o mesmo CPF e vincula o novo, nunca o que já estava lá.
         */
        <Dialogo
          titulo="CPF já cadastrado no SISAC"
          papel="alertdialog"
          descricaoId="paciente-no-sisac-cpf-repetido"
          aoFechar={aoDesistirDoCpfRepetido}
          botoes={
            <>
              <Botao variante="fantasma" onClick={aoDesistirDoCpfRepetido}>
                Voltar
              </Botao>
              {/* Confirmar já envia: a pergunta fecha, e o "Cadastrando…" é o do formulário. */}
              <Botao variante="primario" onClick={() => aoConfirmarCpfRepetido(rascunho)}>
                Cadastrar outro paciente
              </Botao>
            </>
          }
        >
          <p id="paciente-no-sisac-cpf-repetido" className="paciente-no-sisac__nota">
            Encontramos outro paciente com o mesmo CPF no SISAC. Se for a mesma pessoa, volte e
            procure por ela para vincular. Continuar cadastra um paciente novo com este CPF.
          </p>
        </Dialogo>
      )}
      <div className="paciente-no-sisac__acoes">
        <Botao variante="fantasma" onClick={aoCancelar} desabilitado={enviando} motivo={ESPERA}>
          Cancelar
        </Botao>
        <Botao variante="primario" type="submit" desabilitado={enviando} motivo={ESPERA}>
          {enviando ? 'Cadastrando…' : 'Cadastrar e vincular'}
        </Botao>
      </div>
    </form>
  );
}
